import { useState, useCallback } from 'react';
import { DateInputState, InputFormatOptions } from '../types/Input.types';

export const useDateInput = (
  initialValue: string = '',
  format: string = 'dd/MM/yyyy',
  locale: string = 'es-ES'
) => {
  const [state, setState] = useState<DateInputState>({
    isFocused: false,
    inputValue: initialValue,
  });

  const setFocused = useCallback((focused: boolean) => {
    setState((prev) => ({ ...prev, isFocused: focused }));
  }, []);

  const setInputValue = useCallback((value: string) => {
    setState((prev) => ({ ...prev, inputValue: value }));
  }, []);

  const formatDateForInput = useCallback((date: Date | null, options: InputFormatOptions) => {
    if (!date) return '';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return options.format.replace('dd', day).replace('MM', month).replace('yyyy', year.toString());
  }, []);

  const parseDateFromInput = useCallback((inputValue: string, format: string): Date | null => {
    if (!inputValue) return null;

    // Implementar parsing básico según el formato
    const formatRegex = format
      .replace('dd', '(\\d{2})')
      .replace('MM', '(\\d{2})')
      .replace('yyyy', '(\\d{4})');

    const regex = new RegExp(`^${formatRegex}$`);
    const match = inputValue.match(regex);

    if (!match) return null;

    const [, day, month, year] = match;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

    // Validar que la fecha sea válida
    if (isNaN(date.getTime())) return null;

    return date;
  }, []);

  const validateDateInput = useCallback(
    (inputValue: string, format: string): boolean => {
      const date = parseDateFromInput(inputValue, format);
      return date !== null;
    },
    [parseDateFromInput]
  );

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
    },
    [setInputValue]
  );

  const handleInputFocus = useCallback(() => {
    setFocused(true);
  }, [setFocused]);

  const handleInputBlur = useCallback(() => {
    setFocused(false);
  }, [setFocused]);

  return {
    state,
    setFocused,
    setInputValue,
    formatDateForInput,
    parseDateFromInput,
    validateDateInput,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
  };
};
